import type { Mark, TTextLeaf } from '@/textLeaf';

import type { Author, PostHeaderData } from '../../types';

interface GetAuthorNamesTextProps {
  authors: Author[];
  authorUrls?: PostHeaderData['author_urls'];
  truncateList: boolean;
  byLineText: string;
  textStyleAttrs?: Record<string, unknown>;
}

export const getAuthorNamesText = ({ authors, truncateList, byLineText }: GetAuthorNamesTextProps) => {
  const authorCount = authors.length;
  const firstAuthorName = authors[0]?.name;

  const hasMultipleAuthors = authorCount > 1;

  const multipleAuthorsText = truncateList
    ? `${firstAuthorName}, +${authors.length - 1}`
    : authors.map((author) => author.name).join(', ');

  const authorsText = hasMultipleAuthors ? multipleAuthorsText : firstAuthorName;

  return byLineText ? `${byLineText} ${authorsText}` : authorsText;
};

const getLinkMark = (authorUrl: string): Mark => ({
  type: 'link',
  attrs: {
    rel: 'noopener noreferrer',
    href: authorUrl,
    target: '_blank',
    hasUnderline: true,
  },
});

const createTextNode = (text: string, marks: Mark[]): TTextLeaf =>
  ({
    text,
    type: 'text',
    marks,
  }) as TTextLeaf;

const createSeparatorNode = (text: string, textStyleMark: Mark): TTextLeaf => createTextNode(text, [textStyleMark]);

export const getAuthorNamesTextNodes = ({
  authors,
  authorUrls,
  truncateList,
  byLineText,
  textStyleAttrs,
}: GetAuthorNamesTextProps): TTextLeaf[] => {
  const textNodes: TTextLeaf[] = [];
  const textStyleMark: Mark = {
    type: 'textStyle',
    attrs: textStyleAttrs,
  };

  // Add "by" text node if provided
  if (byLineText) {
    textNodes.push(createTextNode(byLineText, [textStyleMark]));
  }

  if (!authorUrls || Object.keys(authorUrls).length === 0) {
    // When no authorUrls, keep all authors together in one node
    const authorsText = getAuthorNamesText({
      authors: authors ?? [],
      truncateList,
      byLineText: '', // byLineText already handled above
    });

    textNodes.push(createTextNode(authorsText, [textStyleMark]));
    return textNodes;
  }

  const createAuthorMarks = (authorId: string): Mark[] => {
    const marks = [textStyleMark];
    const authorUrl = authorUrls[authorId];

    if (authorUrl) {
      marks.unshift(getLinkMark(authorUrl));
    }

    return marks;
  };

  if (!truncateList) {
    authors.forEach((author, index) => {
      // Add author name node
      textNodes.push(createTextNode(author.name, createAuthorMarks(author.id)));

      // Add separator based on position
      if (index === authors.length - 2) {
        textNodes.push(createSeparatorNode(' & ', textStyleMark));
      } else if (index < authors.length - 2) {
        textNodes.push(createSeparatorNode(', ', textStyleMark));
      }
    });
  } else {
    // Truncated list: show first author + count
    const firstAuthor = authors[0];
    textNodes.push(createTextNode(firstAuthor.name, createAuthorMarks(firstAuthor.id)));

    if (authors.length > 1) {
      textNodes.push(createSeparatorNode(`, +${authors.length - 1}`, textStyleMark));
    }
  }

  return textNodes;
};
